// DEV-401 — public Order key validation for the checkout return CTA and the
// Order Hub route.
//
// Mirrors the backend grammar in paworld-content-api
// shared/public/publicOrderKey.ts @ 74d8030:
//
//   UUID_RE              = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
//   CHECKOUT_ORDER_KEY_RE = /^order_from_checkout:cs_[A-Za-z0-9_-]{1,180}$/
//
// with ONE deliberate additional restriction: we reject a deferred key whose
// opaque tail embeds a GUID. The backend's character class happens to admit
// hex-and-hyphen runs, but a Dataverse GUID must never reach a URL, storage,
// telemetry or a log from this app. Rejecting the composite here is strictly
// safer than the backend and can only fail closed.
//
// The legacy UUID form IS itself a GUID-shaped public Order key — that is the
// long-standing public contract for pre-DEV-401 Orders and is unchanged. The
// GUID restriction therefore applies only to the deferred composite form.

import { CHECKOUT_NEVERBLOCK_ENABLED } from "./neverBlockFlag";

/** Exact legacy public Order key: a bare UUID, nothing else. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Never-block deferred Order key. Anchored, so traversal, slashes, query
 * strings, fragments, whitespace and any other punctuation fail closed —
 * the character class admits only [A-Za-z0-9_-].
 */
const CHECKOUT_ORDER_KEY_RE = /^order_from_checkout:cs_[A-Za-z0-9_-]{1,180}$/;

/** A GUID appearing ANYWHERE in the value (not anchored). */
const EMBEDDED_GUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

/** True for a bare legacy UUID Order key. */
export function isLegacyUuidOrderKey(value: string | null | undefined): value is string {
  if (typeof value !== "string") return false;
  return UUID_RE.test(value);
}

/**
 * True for a never-block deferred Order key — but only while the never-block
 * flag is effective. `neverBlock` is injectable so the pure helper stays
 * testable without stubbing module-scope env reads.
 */
export function isCheckoutDeferredOrderKey(
  value: string | null | undefined,
  neverBlock: boolean = CHECKOUT_NEVERBLOCK_ENABLED,
): value is string {
  if (typeof value !== "string") return false;
  if (!neverBlock) return false;
  if (!CHECKOUT_ORDER_KEY_RE.test(value)) return false;
  // Defence in depth: never let a GUID-bearing composite through.
  if (EMBEDDED_GUID_RE.test(value)) return false;
  return true;
}

/**
 * The only two shapes this app will put in an Order Hub URL. Anything else —
 * traversal, slashes, queries, whitespace, mixed junk, GUID-bearing
 * composites, or a deferred key while the flag is OFF — is rejected.
 */
export function isAcceptedPublicOrderKey(
  value: string | null | undefined,
  neverBlock: boolean = CHECKOUT_NEVERBLOCK_ENABLED,
): value is string {
  if (typeof value !== "string" || value.length === 0) return false;
  return isLegacyUuidOrderKey(value) || isCheckoutDeferredOrderKey(value, neverBlock);
}

/**
 * Build the Order Hub path for a key that has ALREADY been validated.
 * Returns null for anything unaccepted, so a caller can never route to a
 * hostile or unsupported key. The single path segment is percent-encoded:
 * the deferred form's ":" becomes "%3A", which the backend route decodes
 * back to the canonical key.
 */
export function buildOrderHubPath(
  value: string | null | undefined,
  neverBlock: boolean = CHECKOUT_NEVERBLOCK_ENABLED,
): string | null {
  if (!isAcceptedPublicOrderKey(value, neverBlock)) return null;
  return `/orders/${encodeURIComponent(value)}`;
}
